import { DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';
import {
  Visit,
  VisitsApi,
  VisitsApiQueryParams,
  VisitsApiSaveParams,
} from '@backstage/plugin-home';

/** Backend plugin id of `@internal/plugin-home-visits-backend`. */
const HOME_VISITS_PLUGIN_ID = 'home-visits';

export const DEFAULT_TOP_VISITS_WINDOW_DAYS = 30;

export interface TopVisitedPage {
  pathname: string;
  title: string;
  entityRef?: string;
  uniqueVisits: number;
  lastVisitedAt: string;
}

export interface TopVisitedResponse {
  windowDays: number;
  pages: TopVisitedPage[];
}

/** Most visited pages across all DevHub users (unique visitors per day, rolling window). */
export async function fetchTopVisited(
  discoveryApi: DiscoveryApi,
  fetchApi: FetchApi,
  limit: number,
): Promise<TopVisitedResponse> {
  const baseUrl = await discoveryApi.getBaseUrl(HOME_VISITS_PLUGIN_ID);
  const response = await fetchApi.fetch(`${baseUrl}/top?limit=${encodeURIComponent(String(limit))}`);
  if (!response.ok) {
    throw new Error(`Failed to load top visited pages (HTTP ${response.status})`);
  }
  const body = (await response.json()) as Partial<TopVisitedResponse>;
  return {
    windowDays: body.windowDays ?? DEFAULT_TOP_VISITS_WINDOW_DAYS,
    pages: Array.isArray(body.pages) ? body.pages : [],
  };
}

export interface TopViewedDocsResponse {
  windowDays: number;
  /** TechDocs entity refs (lower case), most viewed first. */
  entityRefs: string[];
}

/** Most viewed TechDocs sites across all DevHub users (unique visitors per day, rolling window). */
export async function fetchTopViewedDocs(
  discoveryApi: DiscoveryApi,
  fetchApi: FetchApi,
  limit: number,
): Promise<TopViewedDocsResponse> {
  const baseUrl = await discoveryApi.getBaseUrl(HOME_VISITS_PLUGIN_ID);
  const response = await fetchApi.fetch(
    `${baseUrl}/top-docs?limit=${encodeURIComponent(String(limit))}`,
  );
  if (!response.ok) {
    throw new Error(`Failed to load the most viewed docs (HTTP ${response.status})`);
  }
  const body = (await response.json()) as { windowDays?: number; docs?: Array<{ entityRef?: unknown }> };
  return {
    windowDays: body.windowDays ?? DEFAULT_TOP_VISITS_WINDOW_DAYS,
    entityRefs: (Array.isArray(body.docs) ? body.docs : [])
      .map(doc => doc.entityRef)
      .filter((ref): ref is string => typeof ref === 'string' && ref !== ''),
  };
}

/** Converts backend pages to the `Visit` shape used by the stock Recently/Top Visited cards. */
export function toVisits(pages: TopVisitedPage[]): Visit[] {
  return pages.map(page => ({
    id: page.pathname,
    name: page.title || page.pathname,
    pathname: page.pathname,
    hits: page.uniqueVisits,
    timestamp: Date.parse(page.lastVisitedAt) || 0,
    ...(page.entityRef ? { entityRef: page.entityRef } : {}),
  }));
}

/**
 * Keeps the per-user, browser-local visits (Recently Visited) and also reports every visit to
 * the home-visits backend for the all-users Top Visited card. Reporting is fire-and-forget and
 * never fails or delays the local save.
 */
export class RecordingVisitsApi implements VisitsApi {
  constructor(
    private readonly local: VisitsApi,
    private readonly discoveryApi: DiscoveryApi,
    private readonly fetchApi: FetchApi,
  ) {}

  save(saveParams: VisitsApiSaveParams): Promise<Visit> {
    this.report(saveParams).catch(() => undefined);
    return this.local.save(saveParams);
  }

  list(queryParams?: VisitsApiQueryParams): Promise<Visit[]> {
    return this.local.list(queryParams);
  }

  private async report({ visit }: VisitsApiSaveParams): Promise<void> {
    const baseUrl = await this.discoveryApi.getBaseUrl(HOME_VISITS_PLUGIN_ID);
    await this.fetchApi.fetch(`${baseUrl}/visits`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        pathname: visit.pathname,
        title: visit.name,
        ...(visit.entityRef ? { entityRef: visit.entityRef } : {}),
      }),
    });
  }
}
